import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment-timezone';
import { FormattedMessage } from 'react-intl';
import { Card, STRING_TRUE } from '@mummssoftware/utils/constants';
import produce from 'immer';
import { gridContainerStyle } from '@mummssoftware/common-ui';
import {
  MummsPDFGlobalContext,
  SET_PDF_CERT_APP_SNAPSHOT,
} from '@mummssoftware/mumms-print';
import CertHistory from '../CertForm/CertHistory';
import PatientCertHeader from './PatientHeader';
import { CertBodyGrid } from '../CertForm/Components';
import { filterByBenefitStartDateFunc } from '../../utils';

/**
 * @typedef {import("moment-timezone").Moment} Moment
 */

/**
 * @typedef PatientHistoryState
 * @prop {{[k: string]: Object[]}} processedForms
 * @prop {{[k: string]: boolean}} visibleSections
 */

/**
 * @typedef PatientHistoryProps
 * @prop {import('../CertForm/CertForm').CertFormProps} certProps Props for the CertForm component
 * @prop {import('./PatientHeader').PatientHeaderProps} headerProps Props for the PatientHeader component
 * @prop {function} processCerts
 * @prop {function} getCertKeys
 * @prop {function} navCallback
 * @prop {string} [dateFrom] view certs starting from date
 * @prop {string} [dateTo] view certs up until date
 * @prop {boolean} [embedded] Changes view related items
 * @prop {function} formatMessage react-intl i18n function
 * @prop {boolean} [isMobile]
 * @prop {import('../SharedPropDefs').Patient} patient
 * @prop {string} [patientNumber]
 * @prop {string|number} [electionNumber]
 *
 */

const ADD_VISIBLE_SECTION = 'addVisibleSection';
const REMOVE_VISIBLE_SECTION = 'removeVisibleSection';
const SET_PROCESSED_FORMS = 'setProcessedForms';
const REMOVE_PROCESSED_FORM = 'removeProcessedForm';

/**
 * Wrapper for ReactDOM's actually rather stable batchedUpdates function
 * @param {function} func
 */
const batchUpdates = (func) =>
  setTimeout(() => ReactDOM.unstable_batchedUpdates(() => func()), 0);

const setDates = (dateFrom, dateTo, config) => {
  if (dateFrom || dateTo) {
    if (config)
      return {
        startDate: dateFrom,
        endDate: dateTo,
      };
    return {
      startDate: dateFrom ? moment(dateFrom, ['MM-DD-YYYY']) : moment(),
      endDate: dateTo ? moment(dateTo, ['MM-DD-YYYY']) : moment(),
    };
  }
  return {};
};

const NoCerts = () => (
  <CertBodyGrid
    style={{ ...gridContainerStyle['&&'], paddingBottom: '.5rem' }}
    typographyProps={{ color: 'textPrimary' }}
  >
    <FormattedMessage id="history.noMatch" />
  </CertBodyGrid>
);

/**
 * Payer History Wrapper
 */
const CertHistoryWrap = memo(
  ({
    addVisibleSection,
    removeVisibleSection,
    currentSectionVisible,
    certHistoryProps,
    index,
    lastIndex,
    payerId,
    payerForms,
    visibleSectionsLength,
    printConfig,
    mostRecentCert,
    self,
    headerProps,
    patient,
  }) => {
    const { includePdfCertApp, dispatchReactPDF } = useContext(
      MummsPDFGlobalContext,
    );
    const ReactPDFView = includePdfCertApp === STRING_TRUE;

    if (!payerForms.length) {
      if (index === lastIndex && !visibleSectionsLength) {
        // if we need ReactPDF, dispatch the approprate event
        if (ReactPDFView) {
          dispatchReactPDF({
            type: SET_PDF_CERT_APP_SNAPSHOT,
            payload: [],
          });
        }
        return <NoCerts />;
      }
      if (currentSectionVisible) {
        removeVisibleSection(payerId);
      }
      return null;
    }
    if (!currentSectionVisible) {
      addVisibleSection(payerId);
    }
    return (
      <CertHistory
        visibleSectionsLength={ReactPDFView && visibleSectionsLength}
        {...certHistoryProps(
          payerId,
          payerForms,
          printConfig,
          mostRecentCert,
          self,
          headerProps,
        )}
        patient={patient}
      />
    );
  },
);
/**
 * check dates, inclusive
 * @param {Moment} start start moment
 * @param {Moment} end end date
 * @param {Moment} verbalMoment form verbal date
 * @param {Moment} writtenMoment form written date
 */
const filterFunc = (start, end, verbalMoment, writtenMoment) =>
  // '[]' is inclusive, '()' would be exclusive, '(]' exclude start, include end
  verbalMoment.isBetween(start, end, 'days', '[]') ||
  writtenMoment.isBetween(start, end, 'days', '[]');

/**
 * @type {PatientHistoryState}
 */
const initialState = {
  processedForms: {},
  visibleSections: {},
};

function removeForm(forms, action) {
  const index = forms.findIndex(
    ({ formType, certIndex }) =>
      formType === action.formType && certIndex === action.certIndex,
  );
  if (index !== -1) {
    forms.splice(index, 1);
  }
  return forms;
}
/**
 *
 * @param {PatientHistoryState} draft
 * @param {{
      type: string,
      index: number,
      payerId: string,
      processedForms: State['processedForms']
    }} action
 */
function historyReducer(draft, action) {
  switch (action.type) {
    case ADD_VISIBLE_SECTION:
      draft.visibleSections[action.payerId] = true;
      break;
    case REMOVE_VISIBLE_SECTION:
      delete draft.visibleSections[action.payerId];
      break;
    case SET_PROCESSED_FORMS:
      Object.keys(action.processedForms).forEach((payerId) => {
        draft.visibleSections[payerId] = true;
      });
      draft.processedForms = action.processedForms;
      break;
    case REMOVE_PROCESSED_FORM:
      draft.processedForms[action.payerId] = removeForm(
        draft.processedForms[action.payerId],
        action,
      );
      break;
    default:
  }
}

const curriedHistoryReducer = produce(historyReducer);

/**
 * PatientHistory component
 * @param {PatientHistoryProps} props
 */
const PatientHistory = ({
  certProps,
  dateFrom,
  dateTo,
  embedded,
  printConfig,
  formatMessage,
  getCertKeys,
  headerProps,
  navCallback,
  patientNumber,
  patient,
  processCerts,
}) => {
  const self = useRef({
    formatMessage,
    getCertKeys,
    navCallback,
    processCerts,
  });
  const [{ processedForms, visibleSections }, dispatch] = useReducer(
    curriedHistoryReducer,
    initialState,
  );
  const fromDate = printConfig && printConfig.fromDate;
  const toDate = printConfig && printConfig.toDate;
  const dateRange =
    printConfig && fromDate && toDate
      ? setDates(fromDate, toDate, printConfig)
      : setDates(dateFrom, dateTo);
  const [{ startDate, endDate }, setDateState] = useState(dateRange);

  const primaryPayerId = patient.certPayerIDs[0].id;
  const primaryHistory = patient.certHistory[primaryPayerId] || [];
  const mostRecentCert = primaryHistory[primaryHistory.length - 1];

  useEffect(() => {
    const refs = {
      formatMessage,
      getCertKeys,
      navCallback,
      processCerts,
    };
    Object.entries(refs).forEach(([refName, ref]) => {
      if (ref !== self.current[refName]) {
        self.current[refName] = ref;
      }
    });
  }, [formatMessage, getCertKeys, navCallback, processCerts]);

  useEffect(() => {
    setDateState(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, fromDate, toDate]);

  useEffect(() => {
    if (mostRecentCert) {
      self.current.navCallback(mostRecentCert);
    }
  }, [mostRecentCert]);

  // procs when the following change or on load:
  // endDate, patient, startDate]
  useEffect(() => {
    // function start
    const handleForms = async () => {
      const nextProcessedForms = {};
      const promises = [];

      // iterate over each certPayerIds
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < patient.certPayerIDs.length; i++) {
        const { id } = patient.certPayerIDs[i];

        // define setForm function which does the following:
        //
        const setForm = async () => {
          // grab the cert history for the given payerID, medicare, medicaid, ect
          const currentHistory = patient.certHistory[id];
          let forms = await self.current.processCerts(currentHistory);
          let fallbackForm;
          forms = forms.filter(
            ({ complete, verbalComplete }) => !!complete || !!verbalComplete,
          );

          if (!!startDate && !!endDate && !moment(startDate).isAfter(endDate)) {
            forms = forms.filter((form) => {
              const { formType, certIndex } = form;
              const cert = currentHistory[certIndex];
              const {
                formPhysVerbalDate,
                formPhysWrittenDate,
              } = self.current.getCertKeys(formType);
              const verbalMoment = moment(cert[formPhysVerbalDate]);
              const writtenMoment = moment(cert[formPhysWrittenDate]);
              fallbackForm =
                verbalMoment.isSameOrBefore(endDate) ||
                writtenMoment.isSameOrBefore(endDate)
                  ? form
                  : fallbackForm;

              // ultimatly we call filterFunct to filter down the list by a date range.
              return filterFunc(
                startDate,
                endDate,
                verbalMoment,
                writtenMoment,
              );
            });
          }
          if (!forms.length && fallbackForm) {
            forms.push(fallbackForm);
          }

          nextProcessedForms[id] = forms;
        };

        const setPrintForm = async () => {
          // grab the cert history for the given payerID
          const currentHistory = patient.certHistory[id];
          let forms = await self.current.processCerts(currentHistory);

          // only pick up forms that are complete or have a verbal complete
          forms = forms.filter(
            ({ complete, verbalComplete }) => !!complete || !!verbalComplete,
          );

          if (!!startDate && !!endDate && !moment(startDate).isAfter(endDate)) {
            // these need to be moment objects for comparing the date range in the filter
            const startDateMoment = moment(startDate);
            const endDateMoment = moment(endDate);

            forms = forms.filter((form) => {
              const { certIndex } = form;
              const cert = currentHistory[certIndex];

              // if there is no startDate, we cant go any further.  abort!
              if (!cert || !cert.startDate) return false;

              const certEndDate = cert.actualEndDate
                ? cert.actualEndDate
                : cert.expectedEndDate;

              // likewise, if there is no end date, return false.
              if (!certEndDate) return false;

              const benefitStartDateMoment = moment(cert.startDate);
              const benefitEndDateMoment = moment(certEndDate);

              return filterByBenefitStartDateFunc(
                startDateMoment,
                endDateMoment,
                benefitStartDateMoment,
                benefitEndDateMoment,
              );
            });
          }

          nextProcessedForms[id] = forms;
        };
        if (printConfig && printConfig.fromDate && printConfig.toDate) {
          promises.push(setPrintForm());
        } else {
          promises.push(setForm());
        }
      }
      await Promise.all(promises);
      dispatch({
        type: SET_PROCESSED_FORMS,
        processedForms: nextProcessedForms,
      });
    };

    // call the function
    handleForms();
  }, [endDate, patient, startDate]);

  const addVisibleSection = (payerId) => {
    batchUpdates(() => dispatch({ type: ADD_VISIBLE_SECTION, payerId }));
  };

  const removeVisibleSection = (payerId) => {
    batchUpdates(() => dispatch({ type: REMOVE_VISIBLE_SECTION, payerId }));
  };

  const certHistoryProps = useCallback(
    (payerId, forms) => {
      const onHide = (formType, certIndex) => {
        batchUpdates(() =>
          dispatch({
            type: REMOVE_PROCESSED_FORM,
            payerId,
            formType,
            certIndex,
          }),
        );
      };
      return {
        key: `cert-history-${patientNumber}-${payerId}`,
        formatMessage: self.current.formatMessage,
        getCertKeys: self.current.getCertKeys,
        certProps,
        forms,
        onHide,
        patient,
        patientNumber,
        payerId,
        printConfig,
        mostRecentCert,
        self,
        headerProps,
      };
    },
    [
      patientNumber,
      patient,
      certProps,
      printConfig,
      mostRecentCert,
      headerProps,
    ],
  );

  return (
    <Fragment>
      {embedded || (printConfig && printConfig.view === Card) ? null : (
        <PatientCertHeader
          key={`patient-header-${patientNumber}`}
          patient={patient}
          primaryAddress={patient.primaryAddress}
          currentCert={mostRecentCert}
          formatMessage={self.current.formatMessage}
          {...headerProps}
        />
      )}
      {Object.entries(processedForms).map(([payerId, forms], index) => (
        <CertHistoryWrap
          key={`history-body-wrap-${patientNumber}-${payerId}`}
          addVisibleSection={addVisibleSection}
          removeVisibleSection={removeVisibleSection}
          currentSectionVisible={visibleSections[payerId]}
          certHistoryProps={certHistoryProps}
          index={index}
          lastIndex={Object.keys(processedForms).length - 1}
          payerId={payerId}
          payerForms={forms}
          visibleSectionsLength={Object.keys(visibleSections).length}
          printConfig={printConfig}
          mostRecentCert={mostRecentCert}
          self={self}
          headerProps={headerProps}
          patient={patient}
        />
      ))}
    </Fragment>
  );
};

export default memo(PatientHistory);
